import { useCallback, useEffect, useState } from "react";
import { Type } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const field =
    "mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-ink outline-none transition-colors focus:border-brand-blue/50";
const labelCls = "text-[11px] font-bold uppercase tracking-[0.14em] text-ink/45";

const FIELDS = [
    { key: "eyebrow", label: "Eyebrow (chhota pill text upar)", hint: "e.g. Bikaner's First Coworking Space" },
    { key: "line1", label: "Headline — Line 1", hint: "e.g. Work Better." },
    { key: "line2", label: "Headline — Line 2 (gradient wali)", hint: "e.g. Connect Better." },
    { key: "line3", label: "Headline — Line 3", hint: "e.g. Create Better." },
];

export default function HeroTab() {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/api/content/hero");
            setForm(data);
        } catch (e) {
            toast.error(apiError(e, "Failed to load hero content"));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        for (const f of [...FIELDS, { key: "supporting" }]) {
            if (!form[f.key]?.trim()) return toast.error("Sabhi fields required hain");
        }
        setBusy(true);
        try {
            await api.put("/api/admin/content/hero", {
                eyebrow: form.eyebrow.trim(),
                line1: form.line1.trim(),
                line2: form.line2.trim(),
                line3: form.line3.trim(),
                supporting: form.supporting.trim(),
            });
            toast.success("Hero text updated — website pe live ho gaya");
        } catch (e) {
            toast.error(apiError(e, "Failed to save hero content"));
        } finally {
            setBusy(false);
        }
    };

    if (!form) {
        return (
            <div className="flex justify-center py-20" data-testid="hero-tab-loading">
                <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-blue" />
            </div>
        );
    }

    return (
        <div data-testid="hero-tab" className="max-w-xl">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-7">
                <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-purple/10 via-brand-blue/10 to-brand-teal/10 text-brand-purple">
                        <Type size={20} strokeWidth={2.2} />
                    </span>
                    <div>
                        <h3 className="text-base font-bold tracking-tight text-ink">Hero Section Text</h3>
                        <p className="text-xs font-medium text-ink/45">Website ke sabse upar wale section ka text — save karte hi live</p>
                    </div>
                </div>

                <div className="mt-6 space-y-4">
                    {FIELDS.map((f) => (
                        <div key={f.key}>
                            <label className={labelCls}>{f.label}</label>
                            <input
                                data-testid={`hero-field-${f.key}`}
                                value={form[f.key]}
                                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                                className={field}
                                placeholder={f.hint}
                                maxLength={f.key === "eyebrow" ? 80 : 40}
                            />
                        </div>
                    ))}
                    <div>
                        <label className={labelCls}>Supporting Line (headline ke neeche)</label>
                        <textarea
                            data-testid="hero-field-supporting"
                            value={form.supporting}
                            onChange={(e) => setForm((s) => ({ ...s, supporting: e.target.value }))}
                            rows={3}
                            maxLength={160}
                            className={`${field} resize-none`}
                        />
                    </div>
                </div>

                <button
                    data-testid="hero-save-btn"
                    onClick={save}
                    disabled={busy}
                    className="btn-gradient mt-6 rounded-full px-7 py-3 text-sm font-bold text-white disabled:opacity-50"
                >
                    {busy ? "Saving…" : "Save & Publish"}
                </button>
            </div>
        </div>
    );
}
